import { flag } from '../config';

/**
 * Where the patron bottom nav (components/MobileBottomNav.jsx) shows — kept in a module of
 * its own so other chrome (the SLOT_MODEL ConsentBanner, which sits above the nav) can read
 * the same rule without importing the nav component (tests replace that module with a stub).
 * MobileBottomNav re-exports everything here.
 *
 * The nav is mounted only by PublicLayout (and PortalLayout for /email-preferences), so
 * portal pages never need listing here. It only ever renders for a signed-in visitor.
 *
 * SLOT_MODEL on (upgrade brief §2.1): an ALLOW list, the consumer surfaces only
 * (isConsumerSurface): local deals and the venue hub (/local-deals-near-me, with or without
 * ?hub_id), the scan landing (/scan), public business pages (/business/:slug and
 * /business/:slug/:page), the wallet (/deal-wallet) and the profile (/profile). Everywhere
 * else — the B2B marketing pages (/, /for-businesses, /for-venues, /pricing, /how-it-works,
 * /our-story, /venues, /free-listing, /venue/apply), legal pages, auth pages, 404s — it
 * stays hidden: there it took ~60px of a phone screen and competed with the page's CTA.
 *
 * SLOT_MODEL off (the kill switch): the old DENY list, unchanged (chrome golden):
 *   /venue/ (prefix) and /venue (exact)   venue login and unknown venue paths
 *                                          (/venue/login, /venue/x). Since Phase 5c the
 *                                          prefix ends in "/", so the public /venues
 *                                          page (SLOT_MODEL) shows the nav.
 *   /scan, /redeem, /pitch (prefix)        unknown paths that merely start with them
 *                                          (/scan/x, /redeemx, /pitch-deck)
 * Near-miss public slugs (/business/dashboard-bar, /business/deals-galore) follow
 * the matched route (/business/:slug) and keep the nav.
 */
export const BOTTOM_NAV_EXCLUDED = ['/venue/', '/scan', '/redeem', '/pitch'];
export const BOTTOM_NAV_EXCLUDED_EXACT = ['/venue'];

/** SLOT_MODEL off: true on the paths the old deny list hides the nav on. */
export const isBottomNavExcluded = (pathname) =>
  BOTTOM_NAV_EXCLUDED_EXACT.includes(pathname)
  || BOTTOM_NAV_EXCLUDED.some((prefix) => pathname.startsWith(prefix));

/** SLOT_MODEL on: the consumer pages at a fixed path (trailing slashes and case ignored, as the router does). */
export const BOTTOM_NAV_CONSUMER_PATHS = ['/local-deals-near-me', '/scan', '/deal-wallet', '/profile'];

/**
 * The first segment under /business/ that is a business-portal path, never a public
 * business page (the reserved page slugs of backend/repositories/pages.py that live under
 * /business/). With SLOT_MODEL on these portal routes render in the bare PortalLayout
 * anyway; listing them keeps the rule true where a flag leaves one to /business/:slug
 * (e.g. /business/billing while SLOT_BILLING is off).
 */
export const BUSINESS_PORTAL_SEGMENTS = [
  'onboarding', 'dashboard', 'settings', 'deals', 'subscription', 'subscription-manage',
  'subscription-thank-you', 'billing', 'leads', 'reports', 'pages', 'coasters', 'slots', 'offers',
];

const normalise = (pathname) => String(pathname || '/').toLowerCase().replace(/\/+$/, '') || '/';

/**
 * SLOT_MODEL on: true on a consumer surface — BOTTOM_NAV_CONSUMER_PATHS, or a public
 * business page (/business/:slug or /business/:slug/:page whose slug is not a portal
 * segment). Query strings are not part of `pathname` (the venue hub is
 * /local-deals-near-me?hub_id=…).
 */
export const isConsumerSurface = (pathname) => {
  const p = normalise(pathname);
  if (BOTTOM_NAV_CONSUMER_PATHS.includes(p)) return true;
  const m = /^\/business\/([^/]+)(?:\/[^/]+)?$/.exec(p);
  return Boolean(m) && !BUSINESS_PORTAL_SEGMENTS.includes(m[1]);
};

/**
 * True when the bottom nav is on screen below its breakpoint (the nav's own render rule):
 * a signed-in visitor on a consumer surface (SLOT_MODEL on) or on a path the old deny list
 * does not exclude (SLOT_MODEL off).
 */
export const bottomNavShown = (isAuthenticated, pathname) => {
  if (!isAuthenticated) return false;
  return flag('SLOT_MODEL') ? isConsumerSurface(pathname) : !isBottomNavExcluded(pathname);
};
